import React, { useState, useEffect } from 'react';
import { HelpCircle, CheckCircle, XCircle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { supabase } from '../../lib/supabaseClient';
import { Alert, AlertDescription } from '../ui/alert';

const RFQQuestionsManager = ({ rfqId, clientId }) => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    fetchQuestions();
  }, [rfqId]);

  const fetchQuestions = async () => {
    try {
      console.log('Fetching questions for RFQ:', rfqId);

      const { data, error } = await supabase
        .from('rfq_questions')
        .select(`
          id,
          is_enabled,
          client_question:client_questions(
            id,
            question_text,
            is_required,
            display_order
          )
        `)
        .eq('rfq_id', rfqId);

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }

      console.log('Raw data from Supabase:', data);

      // Sort by display_order in JavaScript since Supabase doesn't support ordering by nested fields
      const sortedData = (data || []).sort((a, b) => {
        const orderA = a.client_question?.display_order || 0;
        const orderB = b.client_question?.display_order || 0;
        return orderA - orderB;
      });

      console.log('Sorted questions:', sortedData);
      setQuestions(sortedData);
    } catch (error) {
      console.error('Error fetching RFQ questions:', error);
      setMessage({ type: 'error', text: 'Failed to load questions: ' + error.message });
    } finally {
      setLoading(false);
    }
  };

  const toggleQuestion = async (rfqQuestionId, currentState) => {
    try {
      const { error } = await supabase
        .from('rfq_questions')
        .update({ is_enabled: !currentState })
        .eq('id', rfqQuestionId);

      if (error) throw error;

      // Update local state
      setQuestions(questions.map(q =>
        q.id === rfqQuestionId
          ? { ...q, is_enabled: !currentState }
          : q
      ));

      setMessage({
        type: 'success',
        text: `Question ${!currentState ? 'enabled' : 'disabled'} successfully`
      });
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error('Error toggling question:', error);
      setMessage({ type: 'error', text: 'Failed to toggle question' });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (questions.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <HelpCircle className="h-5 w-5 mr-2" />
            Custom Application Questions
          </CardTitle>
          <CardDescription>
            No custom questions configured for this client
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Custom questions can be added to the client profile and will automatically
            appear here for new RFQs.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center">
          <HelpCircle className="h-5 w-5 mr-2" />
          Custom Application Questions
        </CardTitle>
        <CardDescription>
          {questions.length} question{questions.length !== 1 ? 's' : ''} for this client • Toggle visibility on application form
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {message && (
          <Alert className={message.type === 'success' ? 'bg-green-50 border-green-200 dark:bg-green-950 dark:border-green-800' : 'bg-red-50 border-red-200 dark:bg-red-950 dark:border-red-800'}>
            <AlertDescription className={message.type === 'success' ? 'text-green-800 dark:text-green-200' : 'text-red-800 dark:text-red-200'}>
              {message.text}
            </AlertDescription>
          </Alert>
        )}

        <div className="space-y-3">
          {questions.map((question, index) => (
            <div
              key={question.id}
              className="flex items-start justify-between p-4 border rounded-lg bg-card hover:bg-accent/50 transition-colors"
            >
              <div className="flex-1 space-y-2">
                <div className="flex items-start gap-3">
                  <span className="text-sm text-muted-foreground font-medium mt-0.5">
                    {index + 1}.
                  </span>
                  <div className="flex-1">
                    <p className="font-medium text-foreground">
                      {question.client_question.question_text}
                    </p>
                    <div className="flex items-center gap-2 mt-2">
                      {question.client_question.is_required && (
                        <Badge variant="outline" className="text-xs">
                          Required
                        </Badge>
                      )}
                      <Badge
                        className={
                          question.is_enabled
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                            : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'
                        }
                      >
                        {question.is_enabled ? (
                          <><CheckCircle className="h-3 w-3 mr-1" /> Visible</>
                        ) : (
                          <><XCircle className="h-3 w-3 mr-1" /> Hidden</>
                        )}
                      </Badge>
                    </div>
                  </div>
                </div>
              </div>

              <Button
                variant={question.is_enabled ? "default" : "outline"}
                size="sm"
                onClick={() => toggleQuestion(question.id, question.is_enabled)}
                className="ml-4"
              >
                {question.is_enabled ? 'Hide' : 'Show'}
              </Button>
            </div>
          ))}
        </div>

        <div className="text-xs text-muted-foreground mt-4 pt-4 border-t">
          <p><strong>Note:</strong> Only visible questions will appear on the public job application form. Candidates must answer all visible questions marked as required.</p>
        </div>
      </CardContent>
    </Card>
  );
};

export default RFQQuestionsManager;
